import { ReactNode, useState } from 'react';
import {
  DropEvent,
  DropzoneRootProps,
  FileError,
  FileRejection,
  useDropzone,
} from 'react-dropzone';

import styled, { css } from '@mint-lib/styled';
import * as R from 'ramda';

import { AvatarSize } from '../Avatar/Avatar.constants.js';
import Avatar from '../Avatar/Avatar.jsx';
import Box from '../Box/Box.jsx';
import Button from '../Button/Button.jsx';
import CircularProgress from '../CircularProgress/CircularProgress.jsx';
import FileUploaderItem from '../FileUploaderItem/FileUploaderItem.jsx';
import Icon from '../Icon/Icon.jsx';
import IconButton from '../IconButton/IconButton.jsx';
import Truncation from '../Truncation/Truncation.jsx';
import Typography from '../Typography/Typography.jsx';

type FilePreview = {
  name: string;
  preview: string;
};

type Limitations =
  | {
      maxSize?: never;
      accept?: never;
      maxFiles?: never;
    }
  | {
      maxSize?: number;
      accept?: string | string[];
      maxFiles?: number;
    };

export type FileUploaderProps = {
  onAccept: (files: File[], event?: DropEvent | Event) => void;
  onReject?: (
    fileRejections: FileRejection[],
    event?: DropEvent | Event,
  ) => void;
  disabled?: boolean;
  multiple?: boolean;
  noDrag?: boolean;
  validator?: (file: File) => FileError | Array<FileError>;
  label?: string;
  placeholder?: string;
  errorStringPlaceholder?: string;
  className?: string;
  helperText?: string;
  fullWidth?: boolean;
  CustomFileItems?: ReactNode;
  loading?: boolean;
} & Limitations;

/** @deprecated in favour of new version*/
const FileUploader = ({
  onAccept,
  onReject = () => {},
  validator,
  disabled,
  // Any MIME type
  accept,
  noDrag,
  placeholder,
  errorStringPlaceholder,
  label = 'Upload',
  className = '',
  maxFiles = 0,
  maxSize = Infinity,
  multiple = true,
  fullWidth = false,
  helperText,
  loading = false,
  CustomFileItems,
}: FileUploaderProps) => {
  const [files, setFiles] = useState<FilePreview[]>([]);

  const [errorString, setErrorString] = useState<boolean>(false);
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    open,
    inputRef,
  } = useDropzone({
    disabled,
    maxFiles,
    maxSize,
    accept,
    multiple,
    noDrag,
    validator,
    onDropAccepted: (dropFiles, event) => {
      const filesWithPreview = dropFiles.map((dropFile) => ({
        name: dropFile.name,
        preview: URL.createObjectURL(dropFile),
      }));
      setErrorString(false);
      setFiles(filesWithPreview);
      onAccept(dropFiles, event);
    },
    onDropRejected: (dropFiles, event) => {
      setErrorString(true);
      onReject(dropFiles, event);
    },
  });

  const handleRemoveFile = (fileName: string) => {
    const dt = new DataTransfer();

    if (inputRef.current) {
      // @ts-ignore
      const selectedFiles = Array.from(inputRef.current.files);

      for (const selectedFile of selectedFiles) {
        selectedFile.name !== fileName && dt.items.add(selectedFile);
      }

      inputRef.current.files = dt.files;

      const filesWithPreview = R.reject(
        (curFile: any) => curFile.name === fileName,
        files,
      );

      setFiles(filesWithPreview);
      onAccept(Array.from(dt.files));
    }
  };

  return (
    <UpperContainer className={`container ${className}`} $fullWidth={fullWidth}>
      {label && (
        <Typography
          variant="label01"
          color={disabled ? 'action.disabled01' : 'text.primary'}
        >
          {label}
        </Typography>
      )}

      {noDrag ? (
        <>
          <input {...getInputProps()} />
          <Button
            onClick={open}
            endIcon="Upload"
            variant="outlined"
            color="primary"
            sx={{ width: 'min-content' }}
          >
            {placeholder ?? 'Choose a file'}
          </Button>
        </>
      ) : (
        <Container
          {...getRootProps({ isDragActive, isDragReject, disabled })}
          $loading={loading}
        >
          <input {...getInputProps()} />
          {loading ? (
            <CircularProgress color="primary" />
          ) : (
            (placeholder ?? (
              <Typography variant="bodyShort01">
                Choose a file or drag & drop here
              </Typography>
            ))
          )}
        </Container>
      )}
      {helperText ? (
        <Typography
          variant="helperText01"
          color={disabled ? 'action.disabled01' : 'text.secondary'}
        >
          {helperText}
        </Typography>
      ) : null}
      {errorString ? (
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Icon component="WarningAltFilled" color="warning" />
          <Typography
            variant="helperText01"
            color={disabled ? 'action.disabled01' : 'warning.dark'}
          >
            {errorStringPlaceholder ??
              'The file is too big or is not supported.'}
          </Typography>
        </Box>
      ) : null}

      {CustomFileItems ? (
        CustomFileItems
      ) : files?.length ? (
        <AcceptedFiles disabled={disabled}>
          {files.map((file) => (
            <FileUploaderItem key={file.name}>
              <Avatar src={file.preview} avatarSize={AvatarSize.xsmall} />
              <Truncation tooltipTitle={file.name}>{file.name}</Truncation>
              <IconButton
                icon="Delete"
                size="small"
                variant="icon"
                color="primary"
                onClick={() => handleRemoveFile(file.name)}
              />
            </FileUploaderItem>
          ))}
        </AcceptedFiles>
      ) : null}
    </UpperContainer>
  );
};

export default FileUploader;

const getColor = (props: DropzoneRootProps) => {
  if (props.isDragActive) {
    return css`
      border-color: ${props.theme.palette.tertiary.main};
      background-color: ${props.theme.palette.background.tertiary};
    `;
  }
  if (props.disabled) {
    return css`
      cursor: not-allowed;
      pointer-events: none;
      background-color: ${props.theme.palette.ui.primary};
      border-color: ${props.theme.palette.action.disabledBackground};
      color: ${props.theme.palette.action.disabled01};
    `;
  }
  if (props.$loading) {
    return css`
      cursor: not-allowed;
      pointer-events: none;
      background-color: ${props.theme.palette.ui.primary};
      color: ${props.theme.palette.action.disabled01};
    `;
  }
  return css``;
};

const UpperContainer = styled('div')<{
  $fullWidth: boolean;
  disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${(props) => (props.$fullWidth ? '100%' : '17.5rem')};
`;

const Container = styled('div')<DropzoneRootProps>`
  min-height: 5rem;
  height: 5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1.5px dashed;
  border-radius: 8px;
  ${({ theme }) => {
    return css`
      background-color: ${theme.palette.ui.primary};
      color: ${theme.palette.text.link};
      outline: none;
      border-color: ${theme.palette.ui.tertiary};
      &:hover {
        border-color: ${theme.palette.tertiary.main};
        background-color: ${theme.palette.background.tertiary};
      }
    `;
  }};
  ${(props) => getColor(props)}
  border-color: ${({ theme }) =>
    `${theme.palette.secondary.main} transparent transparent transparent`};
`;

const AcceptedFiles = styled('div')<{
  disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
  max-height: 12.5rem;
  ${(props) =>
    props.disabled && `cursor: not-allowed; pointer-events: none; opacity: 0.5`}
`;
